import type {
  ApiMembershipTermsOfUse,
  ApiMembershipTermsOfUseAcceptance,
  ApiSaveMembershipTermsOfUse,
} from '../schemas/termsOfUse.schema';
import type { BaseApiResponse } from './types/api.types';

import { post, get } from './axios';

const baseUrl = (organizationId: string) =>
  `/v2/organizations/${organizationId}/membership-terms-of-use`;

export async function fetchDefaultMembershipTermsOfUse({
  organizationId,
}: {
  organizationId: string;
}) {
  const response = await get<BaseApiResponse<ApiMembershipTermsOfUse>>(
    `${baseUrl(organizationId)}/default`,
  );
  return response?.data ?? null;
}

export async function saveDefaultMembershipTermsOfUse({
  organizationId,
  data,
}: {
  organizationId: string;
  data: ApiSaveMembershipTermsOfUse;
}) {
  const response = await post<ApiMembershipTermsOfUse>(
    `${baseUrl(organizationId)}/default`,
    data,
  );
  return response?.data ?? null;
}

export async function fetchMembershipTermsOfUse({
  organizationId,
  membershipId,
}: {
  organizationId: string;
  membershipId: string;
}) {
  const response = await get<BaseApiResponse<ApiMembershipTermsOfUse>>(
    `${baseUrl(organizationId)}/memberships/${membershipId}`,
  );
  return response?.data ?? null;
}

export async function saveMembershipTermsOfUse({
  organizationId,
  membershipId,
  data,
}: {
  organizationId: string;
  membershipId: string;
  data: ApiSaveMembershipTermsOfUse;
}) {
  const response = await post<ApiMembershipTermsOfUse>(
    `${baseUrl(organizationId)}/memberships/${membershipId}`,
    data,
  );
  return response?.data ?? null;
}

export async function resendMembershipTermsOfUseContactEmailVerification({
  organizationId,
  membershipId,
  contactEmail,
}: {
  organizationId: string;
  membershipId: string;
  contactEmail: string;
}) {
  await post(
    `${baseUrl(organizationId)}/memberships/${membershipId}/resend-contact-email-verification`,
    { contactEmail },
  );
}

export async function fetchUserMembershipTermsOfUse({
  organizationId,
  clientId,
  userMembershipId,
}: {
  organizationId: string;
  clientId: string;
  userMembershipId: string;
}) {
  const response = await get<BaseApiResponse<ApiMembershipTermsOfUse>>(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships/${userMembershipId}/terms-of-use`,
  );
  return response?.data ?? null;
}

export async function acceptMembershipTermsOfUse({
  organizationId,
  clientId,
  userMembershipId,
  versionId,
}: {
  organizationId: string;
  clientId: string;
  userMembershipId: string;
  versionId: string;
}) {
  const response = await post<{ accepted: boolean }>(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships/${userMembershipId}/terms-of-use/accept`,
    { versionId },
  );
  return response?.data ?? null;
}

export async function fetchUserMembershipTermsOfUseAcceptance({
  organizationId,
  clientId,
  userMembershipId,
}: {
  organizationId: string;
  clientId: string;
  userMembershipId: string;
}) {
  const response = await get<
    BaseApiResponse<ApiMembershipTermsOfUseAcceptance | null>
  >(
    `/v2/organizations/${organizationId}/clients/${clientId}/memberships/${userMembershipId}/terms-of-use/acceptance`,
  );
  return response?.data ?? null;
}
